import type { Layout, PlotData } from "plotly.js";
import { PlotCard } from "../PlotCard";
import type { JfPredictionPayload } from "@tbm/shared";

interface JFPlotProps {
  payload: JfPredictionPayload | null;
  driveLengthM: number | null;
}

/**
 * Trace colours / dash styles match the legacy JFPrediction.js so the
 * new portal looks the same as what the user is used to.
 */
export function JFPlot({ payload, driveLengthM }: JFPlotProps) {
  if (!payload) {
    return (
      <PlotCard
        title="Jacking Force prediction"
        subtitle="Save the drive setup to compute an initial prediction."
        data={[]}
        layout={emptyLayout(driveLengthM)}
        height={420}
        uirevision="jacking-force-empty"
      />
    );
  }

  const x = payload.chainageM;

  const traces: Partial<PlotData>[] = [
    {
      x: payload.measured.chainageM,
      y: payload.measured.jackingForceKn,
      type: "scattergl",
      mode: "markers",
      name: "Measured JF",
      marker: { color: "rgba(110,110,110,0.55)", size: 4 },
    },
    {
      x,
      y: payload.lowerST0,
      type: "scatter",
      mode: "lines",
      name: "ST0 lower",
      line: { color: "rgba(0,123,255,0.0)", width: 0 },
      hoverinfo: "skip",
      showlegend: false,
    },
    {
      x,
      y: payload.upperST0,
      type: "scatter",
      mode: "lines",
      name: "95% confidence",
      line: { color: "rgba(0,123,255,0.0)", width: 0 },
      fill: "tonexty",
      fillcolor: "rgba(0,123,255,0.18)",
      hoverinfo: "skip",
    },
    {
      x,
      y: payload.meanST0,
      type: "scatter",
      mode: "lines",
      name: "JF",
      line: { color: "#1f6feb", width: 2 },
    },
    {
      x,
      y: payload.meanST1,
      type: "scatter",
      mode: "lines",
      name: "12 hour JF",
      line: { color: "#22a06b", width: 1.5 },
    },
    ...(payload.meanSTC && payload.customStoppageHours
      ? [
          {
            x,
            y: payload.meanSTC,
            type: "scatter" as const,
            mode: "lines" as const,
            name: `${payload.customStoppageHours} hour JF`,
            line: { color: "#8957e5", width: 1.5 },
          },
        ]
      : []),
    {
      x,
      y: payload.CPredKn,
      type: "scatter",
      mode: "lines",
      name: "TBM force",
      line: { color: "#d97706", width: 1.5, dash: "dash" },
    },
    {
      x,
      y: payload.sfPredKpa,
      type: "scatter",
      mode: "lines",
      name: "Skin friction",
      yaxis: "y2",
      line: { color: "#c8102e", width: 1.5, dash: "dashdot" },
    },
  ];

  return (
    <PlotCard
      title="Jacking Force prediction"
      data={traces}
      layout={layout(driveLengthM)}
      height={420}
      uirevision="jacking-force"
    />
  );
}

function layout(driveLengthM: number | null): Partial<Layout> {
  return {
    xaxis: {
      title: { text: "Chainage (m)" },
      range: driveLengthM ? [0, driveLengthM] : undefined,
      zeroline: false,
    },
    yaxis: {
      title: { text: "Jacking Force (kN)" },
      rangemode: "tozero",
    },
    yaxis2: {
      title: { text: "Skin Friction (kPa)" },
      overlaying: "y",
      side: "right",
      rangemode: "tozero",
      showgrid: false,
    },
    showlegend: true,
    legend: {
      orientation: "h",
      y: -0.22,
      x: 0,
      font: { size: 11 },
    },
    hovermode: "x unified",
    margin: { l: 60, r: 60, t: 16, b: 70 },
  };
}

function emptyLayout(driveLengthM: number | null): Partial<Layout> {
  return {
    ...layout(driveLengthM),
    annotations: [
      {
        text: "No prediction yet — open Drive setup and Save.",
        showarrow: false,
        xref: "paper",
        yref: "paper",
        x: 0.5,
        y: 0.5,
        font: { size: 13, color: "#8b8b8b" },
      },
    ],
  };
}
